/**
 * Credit Karma cashback offer — shared targeting + reward-tier rules.
 *
 * Single source of truth for:
 *   - who is "credit-blocked" enough to be worth showing the offer to
 *   - whether a member sees the public $3 tier or the better-than-public $5 tier
 *
 * Used by the public landing page, the credit-match onboarding page, the
 * dashboard card, and the server-side email nudge so all four agree.
 *
 * The cashback is funded by Home Approach out of pocket — no partner pays for
 * placement. Keep the funding disclosure copy wherever this offer renders.
 */

export interface CreditOfferReward {
  dollars: number;
  points: number;
  /** "member" when the member is on the better-than-public tier. */
  tier: "public" | "member";
}

export const PUBLIC_REWARD: CreditOfferReward = { dollars: 3, points: 300, tier: "public" };
export const MEMBER_REWARD: CreditOfferReward = { dollars: 5, points: 500, tier: "member" };

/**
 * Lead sources that earn the better-than-public $5 tier alongside LifeMart:
 * Ruben's landing page and the Hero Stack co-expert funnel.
 */
export const UPLIFT_LEAD_SOURCES = ["ruben", "hero-stack"] as const;

export function isUpliftLeadSource(leadSource: string | null | undefined): boolean {
  if (!leadSource) return false;
  return (UPLIFT_LEAD_SOURCES as readonly string[]).includes(leadSource.trim().toLowerCase());
}

/**
 * $5 for LifeMart-branded visitors and Ruben / Hero Stack signups, $3 for
 * everyone else.
 */
export function creditOfferReward(opts: {
  isLifeMart?: boolean;
  leadSource?: string | null;
}): CreditOfferReward {
  if (opts.isLifeMart || isUpliftLeadSource(opts.leadSource)) return MEMBER_REWARD;
  return PUBLIC_REWARD;
}

/**
 * Parse the first score number out of the many historical shapes stored in
 * user_attributes.credit_score_range ("620–659", "below_620", "740_plus",
 * "Below 580 (Poor)", "Not sure", "good", ...).
 *
 * Returns null when the range carries no usable number.
 */
export function creditRangeFloor(range: string | null | undefined): number | null {
  if (!range) return null;
  const match = range.match(/\d{3}/);
  if (!match) return null;
  const value = Number(match[0]);
  if (Number.isNaN(value)) return null;
  // "below_620" / "Below 580" describe an upper bound, not a floor.
  if (/below|under|less/i.test(range)) return value - 1;
  return value;
}

/** Score bands we treat as credit-blocked. Unknown counts as blocked. */
export const CREDIT_BLOCKED_CEILING = 660;

export function isCreditBlockedRange(range: string | null | undefined): boolean {
  const floor = creditRangeFloor(range);
  if (floor === null) return true; // unknown or non-numeric ("Not sure", "good", null)
  return floor < CREDIT_BLOCKED_CEILING;
}

export interface CreditOfferEligibilityInput {
  /** user_attributes.credit_score_range */
  creditScoreRange?: string | null;
  /** profiles.credit_score — a confirmed self-reported score wins over the range */
  creditScore?: number | null;
  /** user_attributes.pre_approval_status */
  preApprovalStatus?: string | null;
  /** profiles.credit_offer_claimed_at */
  creditOfferClaimedAt?: string | null;
  /** true when the member has an open lender introduction */
  hasActiveLenderIntro?: boolean;
}

export type CreditOfferIneligibleReason =
  | "already_claimed"
  | "pre_approved"
  | "credit_ready"
  | "active_lender_intro";

export interface CreditOfferEligibility {
  eligible: boolean;
  reason?: CreditOfferIneligibleReason;
}

/**
 * A member sees the offer only when credit is the thing standing between them
 * and a mortgage conversation — and only when we aren't already mid-intro.
 */
export function evaluateCreditOfferEligibility(
  input: CreditOfferEligibilityInput,
): CreditOfferEligibility {
  if (input.creditOfferClaimedAt) return { eligible: false, reason: "already_claimed" };
  if (input.preApprovalStatus === "pre_approved") return { eligible: false, reason: "pre_approved" };
  if (input.hasActiveLenderIntro) return { eligible: false, reason: "active_lender_intro" };

  if (typeof input.creditScore === "number" && input.creditScore > 0) {
    return input.creditScore < CREDIT_BLOCKED_CEILING
      ? { eligible: true }
      : { eligible: false, reason: "credit_ready" };
  }

  return isCreditBlockedRange(input.creditScoreRange)
    ? { eligible: true }
    : { eligible: false, reason: "credit_ready" };
}

/** Lender-intro statuses that mean a live conversation we must not interrupt. */
export const ACTIVE_LENDER_INTRO_STATUSES = [
  "pending",
  "notified",
  "sent",
  "contacted",
  "in_progress",
];

/**
 * Best-effort lead source for an ANONYMOUS visitor, so the public landing page
 * can show the same tier the member will actually receive after signup. Reads
 * the attribution values already persisted by src/lib/attribution.ts; returns
 * null during SSR or when storage is unavailable.
 */
export function getStoredLeadSource(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return (
      localStorage.getItem("lifemart_signup_source") ||
      localStorage.getItem("ha_lead_source") ||
      new URLSearchParams(window.location.search).get("source")
    );
  } catch {
    return null;
  }
}
